/**
 * Product Analytics API (via pro-api BFF) — the dashboard/read side of the
 * in-house session replay + error capture system. Ingestion (recording) side
 * lives in src/app/lib/monitoring/product-analytics.ts; this file is the
 * consumer of what that (and smart-web, and pro-web's own error handlers)
 * sends into analytics-service.
 *
 * Cursor-paginated, mirroring src/app/lib/api/history.ts's shape exactly.
 */
import { authenticatedProApiFetch, API_BASE_URL } from "./pro-api.client";

export interface AnalyticsSession {
  id: string;
  appSource: string;
  organizationId: string | null;
  userId: string | null;
  startedAt: string;
  endedAt: string | null;
  durationMs: number | null;
  userAgent: string | null;
  urlPath: string | null;
  appVersion: string | null;
  chunkCount: number;
  totalBytes: number;
  hasErrors: boolean;
  status: string;
  createdAt: string;
}

export interface AnalyticsErrorEvent {
  id: string;
  appSource: string;
  organizationId: string | null;
  userId: string | null;
  sessionId: string | null;
  kind: string;
  message: string;
  stack: string | null;
  urlPath: string | null;
  httpMethod: string | null;
  httpStatus: number | null;
  httpUrl: string | null;
  fingerprint: string | null;
  severity: string;
  appVersion: string | null;
  meta: Record<string, unknown> | null;
  occurredAt: string;
  createdAt: string;
}

export interface AnalyticsTrace {
  id: string;
  xrayTraceId: string;
  rootServiceName: string | null;
  durationMs: number | null;
  hasError: boolean;
  hasFault: boolean;
  startedAt: string;
}

export interface AnalyticsSessionDetail {
  session: AnalyticsSession;
  chunkManifest: { seq: number; sizeBytes: number }[];
  linkedErrors: AnalyticsErrorEvent[];
  linkedTraces: AnalyticsTrace[];
}

export interface AnalyticsReplayChunk {
  seq: number;
  url: string;
}

export interface AnalyticsFeedParams {
  limit: number;
  cursor?: string | null;
  order?: "asc" | "desc";
  filters?: Record<string, unknown>;
}

export interface AnalyticsFeedPage<T> {
  data: T[];
  nextCursor: string | null;
  hasMore: boolean;
}

function buildFeedQuery(params: AnalyticsFeedParams): string {
  const qs = new URLSearchParams();
  qs.set("limit", String(params.limit));
  if (params.cursor) qs.set("cursor", params.cursor);
  if (params.order) qs.set("order", params.order);
  if (params.filters && Object.keys(params.filters).length > 0) {
    qs.set("filters", JSON.stringify(params.filters));
  }
  return qs.toString();
}

export async function fetchAnalyticsSessionsFeed(
  accessToken: string,
  params: AnalyticsFeedParams,
): Promise<AnalyticsFeedPage<AnalyticsSession>> {
  const query = buildFeedQuery(params);
  const data = await authenticatedProApiFetch<{
    data?: AnalyticsSession[];
    nextCursor?: string | null;
    hasMore?: boolean;
  }>(`/analytics/sessions?${query}`, accessToken, { method: "GET" });
  return {
    data: data.data ?? [],
    nextCursor: data.nextCursor ?? null,
    hasMore: Boolean(data.hasMore),
  };
}

export async function fetchAnalyticsSessionDetail(
  accessToken: string,
  sessionId: string,
): Promise<AnalyticsSessionDetail> {
  return authenticatedProApiFetch<AnalyticsSessionDetail>(
    `/analytics/sessions/${sessionId}`,
    accessToken,
    { method: "GET" },
  );
}

export async function fetchAnalyticsSessionReplay(
  accessToken: string,
  sessionId: string,
): Promise<AnalyticsReplayChunk[]> {
  const data = await authenticatedProApiFetch<{ chunks?: AnalyticsReplayChunk[] }>(
    `/analytics/sessions/${sessionId}/replay`,
    accessToken,
    { method: "GET" },
  );
  // Local dev (BLOB_STORAGE=local): pro-api returns a path relative to its
  // own /api mount (proxying analytics-service's local blob store, which is
  // never reachable directly from the browser). Deployed envs return
  // absolute presigned S3 URLs, fetched directly — left untouched.
  return (data.chunks ?? []).map((chunk) =>
    chunk.url.startsWith("/") ? { ...chunk, url: `${API_BASE_URL}${chunk.url}` } : chunk,
  );
}

export async function fetchAnalyticsErrorsFeed(
  accessToken: string,
  params: AnalyticsFeedParams,
): Promise<AnalyticsFeedPage<AnalyticsErrorEvent>> {
  const query = buildFeedQuery(params);
  const data = await authenticatedProApiFetch<{
    data?: AnalyticsErrorEvent[];
    nextCursor?: string | null;
    hasMore?: boolean;
  }>(`/analytics/errors?${query}`, accessToken, { method: "GET" });
  return {
    data: data.data ?? [],
    nextCursor: data.nextCursor ?? null,
    hasMore: Boolean(data.hasMore),
  };
}

export interface AnalyticsErrorDetail {
  error: AnalyticsErrorEvent;
  session: { id: string; appSource: string; startedAt: string; durationMs: number | null } | null;
}

export async function fetchAnalyticsErrorDetail(
  accessToken: string,
  errorId: string,
): Promise<AnalyticsErrorDetail> {
  return authenticatedProApiFetch<AnalyticsErrorDetail>(
    `/analytics/errors/${errorId}`,
    accessToken,
    { method: "GET" },
  );
}
